import { useState, useCallback } from "react";
import { exportOccurrencesToExcel } from "../utils/exportOccurencesToExcel";
import { Occurrence } from "../models/occurrence";
import { fetchOccurrence } from "../api/fetchOccurence";
import { OccurrenceStepEnum } from "../enums/occurrenceStep";
import ApiService from "../services/api";

type Stage = "writing" | "done" | "error" | "paging";

export interface ExportFilters {
  criticality?: number;
  createdStartAt?: string;
  createdEndAt?: string;
  updatedStartAt?: string;
  updatedEndAt?: string;
  occurrenceType?: string[];
  // createdByUuid?: string;
  dealershipDn?: string[];
  hasCampaigns?: boolean;
  stepType?: OccurrenceStepEnum;
  chassisList?: string[];
  finishedFilter?: string[];
  regional?: string[];
  cell?: string[];
}

export function useOccurrenceExport() {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("paging");
  const [progress, setProgress] = useState(0);
  const [detail, setDetail] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const close = () => setOpen(false);

  const exportWithFilters = useCallback(
    async (args: {
      filters: ExportFilters;
      filename?: string;
      userRole: "TOWER" | "CONSULTANT" | "MANAGER" | "PARTNERSHIP";
    }) => {
      const { filters, filename = `kanban_${new Date().toISOString().slice(0, 10)}.xlsx`, userRole } = args;

      setOpen(true);
      setStage("writing");
      setProgress(50);
      setDetail('Preparando exportação...');
      setErrorMessage(undefined);

      try {
        const apiService = new ApiService();

        const queryParams = new URLSearchParams();

        if (filters.criticality !== undefined && !isNaN(filters.criticality)) {
          queryParams.append('criticality', filters.criticality.toString());
        }

        if (filters.createdStartAt && filters.createdEndAt) {
          queryParams.append('createdStartAt', filters.createdStartAt);
          queryParams.append('createdEndAt', filters.createdEndAt);
        }

        if (filters.updatedStartAt && filters.updatedEndAt) {
          queryParams.append('updatedStartAt', filters.updatedStartAt);
          queryParams.append('updatedEndAt', filters.updatedEndAt);
        }

        if (filters.occurrenceType && filters.occurrenceType.length > 0) {
          const filteredTypes = filters.occurrenceType.filter(type => type !== 'HAS_CAMPAIGNS');

          filteredTypes.forEach(type => {
            queryParams.append('occurrenceType', type);
          });

          if (filters.occurrenceType.includes('HAS_CAMPAIGNS')) {
            queryParams.append('hasCampaigns', 'true');
          }
        } else if (filters.hasCampaigns !== undefined) {
          queryParams.append('hasCampaigns', filters.hasCampaigns.toString());
        }

        if (filters.dealershipDn && filters.dealershipDn.length > 0) {
          filters.dealershipDn.forEach(dn => {
            queryParams.append('dealershipDn', dn);
          });
        }

        if ((filters as any).regional && (filters as any).regional.length > 0) {
          (filters as any).regional.forEach((r: string) => queryParams.append('regional', r));
        }

        if ((filters as any).cell && (filters as any).cell.length > 0) {
          (filters as any).cell.forEach((c: string) => queryParams.append('cell', c));
        }

        if (filters.stepType && filters.stepType.trim()) {
          queryParams.append('stepType', filters.stepType);
        }

        let urlSuffix = '';

        if (filters.finishedFilter && filters.finishedFilter.length > 0) {
          if (filters.finishedFilter.includes('true') && filters.finishedFilter.includes('false')) {
            urlSuffix = '/all';
          } else if (filters.finishedFilter.includes('true') && !filters.finishedFilter.includes('false')) {
            urlSuffix = '/finished';
          } else if (filters.finishedFilter.includes('false') && !filters.finishedFilter.includes('true')) {
            urlSuffix = '';
          } else {
            urlSuffix = '/all';
          }
        } else {
          urlSuffix = '/all';
        }

        const requestUrl = `/occurrence/sheet/steps/${userRole}${urlSuffix}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        setProgress(75);
        setDetail('Baixando arquivo...');

        const requestBody = {
          chassisList: filters.chassisList || []
        };

        const response = await apiService.post(requestUrl, requestBody, { responseType: 'blob' });

        setProgress(100);
        setDetail('');

        const downloadUrl = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        setStage("done");
      } catch (e: any) {
        console.error('Export error:', e);
        setStage("error");
        setErrorMessage(e?.response?.data?.message || e?.message || "Falha ao exportar.");
      }
    },
    []
  );

  const runAll = useCallback(
    async (args: {
      pager: {
        fetchPage: (page: number, size: number) => Promise<Occurrence[]>;
      };
      occurrenceStepAvailable: string[];
      filename: string;
      userRole: "TOWER" | "CONSULTANT" | "MANAGER" | "PARTNERSHIP";
      pageSize?: number;
      estimatePages?: number;
      maxPages?: number;
      getKey?: (o: Occurrence) => string;
    }) => {
      const {
        pager,
        occurrenceStepAvailable,
        filename,
        userRole,
        pageSize = 20,
        estimatePages = 10,
        maxPages = 300,
        getKey,
      } = args;
      setOpen(true);
      setStage("paging");
      setProgress(0);
      setDetail(undefined);
      setErrorMessage(undefined);

      try {
        const all: Occurrence[] = [];
        let page = 0;

        while (page < maxPages) {
          const rows = await pager.fetchPage(page, pageSize);
          // Busca os dados completos de cada ocorrência
          const fullRows = await Promise.all(
            rows.map(async (row) => {
              // Se já tem protocolNumber ou scheduleNumber, retorna direto
              if (row.protocolNumber || row.scheduleNumber) return row;
              // Busca pelo id
              if (row.id) {
                const full = await fetchOccurrence(row.id, userRole);
                return full ?? row;
              }
              return row;
            })
          );
          all.push(...fullRows);

          const totalEst = Math.max(1, estimatePages);
          const pct = Math.min(99, Math.round(((page + 1) / totalEst) * 100));
          setProgress(pct);
          setDetail(`${page + 1}${rows.length < pageSize ? "" : `+`}`);

          if (rows.length < pageSize) break;

          page += 1;
        }

        setStage("writing");
        setProgress(100);

        await exportOccurrencesToExcel({
          occurrenceList: all,
          occurrenceStepAvailable,
          filename,
          onProgress: () => {},
        });

        setStage("done");
      } catch (e: any) {
        setStage("error");
        setErrorMessage(e?.message ?? "Falha ao exportar.");
      }
    },
    []
  );

  return { open, stage, progress, detail, errorMessage, close, runAll, exportWithFilters };
}
